import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { providersAPI, reviewsAPI, bookingsAPI, uploadAPI, getShareUrl } from '../lib/api';
import { extUrl } from '../lib/url';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Search, Star, MapPin, CheckCircle, Calendar, Phone, Mail,
  ExternalLink, Loader2, Briefcase, ChevronLeft, ChevronRight,
  Heart, Globe, MessageCircle, Plus, CalendarDays, Crown, ArrowRight, Check,
  Eye, Sparkles, Home, Scale, Stethoscope, GraduationCap, Wrench, Scissors,
  Camera, Music, Palette, Dumbbell, UtensilsCrossed, Plane, X,
  Linkedin, Instagram, Video, Link2, Building2, ShoppingBag,
  QrCode, Download, Copy, Share2, Trash2, Edit2, Award, Upload
} from 'lucide-react';

// Detect special credentials so we can render them with distinct icons / styling.
const FOUNDING_PROVIDER_CREDENTIAL = 'GradLink Founding Provider';
const isFoundingProvider = (creds) =>
  Array.isArray(creds) && creds.some(c => /founding provider/i.test(c));

const HBCU_NAME_FROM_CRED = /^hbcu alum\s*:\s*(.+)$/i;

// Pull the first "HBCU Alum: <school>" entry out of a credentials array.
const getHbcuFromCredentials = (creds) => {
  if (!Array.isArray(creds)) return null;
  for (const c of creds) {
    const m = String(c || '').match(HBCU_NAME_FROM_CRED);
    if (m) return m[1].trim();
  }
  return null;
};

const credentialDisplay = (raw) => {
  const c = String(raw || '').trim();
  if (/founding provider/i.test(c)) {
    return { icon: Award, label: c, classes: 'bg-mint-fresh/15 text-emerald-800 border-mint-fresh/40' };
  }
  if (/^hbcu alum/i.test(c)) {
    const m = c.match(HBCU_NAME_FROM_CRED);
    const schoolName = m ? m[1].trim() : null;
    const logoUrl = schoolName ? getHbcuLogoUrl(schoolName) : null;
    return {
      icon: GraduationCap,
      label: c,
      logoUrl,
      classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    };
  }
  return { icon: CheckCircle, label: c, classes: 'bg-slate-50 text-slate-700 border-slate-200' };
};

// Renders an HBCU logo from a CDN with a graceful icon fallback if the image fails.
function CredentialLogo({ src, fallback: Fallback }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) return <Fallback className="w-4 h-4 flex-shrink-0" />;
  return (
    <img
      src={src}
      alt=""
      className="w-4 h-4 rounded-sm object-contain flex-shrink-0 bg-white"
      onError={() => setErrored(true)}
      loading="lazy"
    />
  );
}
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import LocationAutocomplete from '../components/LocationAutocomplete';
import usePageTitle from '../hooks/usePageTitle';
import ProfilePreview from '../components/ProfilePreview';
import ShareMenu from '../components/ShareMenu';
import { getHbcuLogoUrl } from '../lib/hbcuList';

// ── Service area tiles with icons ──
const SERVICE_AREAS = [
  { value: 'career_coach', label: 'Career Coach', icon: Star, color: 'bg-amber-50 text-amber-600 border-amber-200' },
  { value: 'real_estate', label: 'Realtor', icon: Home, color: 'bg-blue-50 text-blue-600 border-blue-200' },
  { value: 'financial', label: 'Financial Advisor', icon: Briefcase, color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  { value: 'legal', label: 'Attorney', icon: Scale, color: 'bg-purple-50 text-purple-600 border-purple-200' },
  { value: 'healthcare', label: 'Healthcare', icon: Stethoscope, color: 'bg-red-50 text-red-600 border-red-200' },
  { value: 'therapist', label: 'Therapist', icon: Heart, color: 'bg-pink-50 text-pink-600 border-pink-200' },
  { value: 'tutor', label: 'Tutor', icon: GraduationCap, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  { value: 'travel', label: 'Travel Agent', icon: Plane, color: 'bg-sky-50 text-sky-600 border-sky-200' },
  { value: 'home_repair', label: 'Home Repair', icon: Wrench, color: 'bg-orange-50 text-orange-600 border-orange-200' },
  { value: 'barber', label: 'Barber', icon: Scissors, color: 'bg-slate-50 text-slate-600 border-slate-200' },
  { value: 'cosmetology', label: 'Cosmetology', icon: Sparkles, color: 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200' },
  { value: 'photographer', label: 'Photographer', icon: Camera, color: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
  { value: 'personal_trainer', label: 'Personal Trainer', icon: Dumbbell, color: 'bg-lime-50 text-lime-600 border-lime-200' },
  { value: 'caterer', label: 'Caterer', icon: UtensilsCrossed, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  { value: 'restaurants', label: 'Restaurants', icon: UtensilsCrossed, color: 'bg-rose-50 text-rose-600 border-rose-200' },
  { value: 'event_management', label: 'Event Management', icon: CalendarDays, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  { value: 'elder_care', label: 'Elder Care', icon: Heart, color: 'bg-teal-50 text-teal-600 border-teal-200' },
  { value: 'fashion', label: 'Fashion', icon: ShoppingBag, color: 'bg-pink-50 text-pink-600 border-pink-200' },
  { value: 'music_instructor', label: 'Music Instructor', icon: Music, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  { value: 'graphic_designer', label: 'Graphic Designer', icon: Palette, color: 'bg-teal-50 text-teal-600 border-teal-200' },
];

const DEFAULT_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  ...SERVICE_AREAS.map(s => ({ value: s.value, label: s.label })),
];

const StarRating = ({ rating, size = 'sm', interactive = false, onChange }) => (
  <div className="flex items-center">
    {[1, 2, 3, 4, 5].map(s => (
      <button key={s} onClick={() => interactive && onChange?.(s)} className={interactive ? 'cursor-pointer' : 'cursor-default'} type="button">
        <Star className={`${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} ${s <= rating ? 'text-amber-400 fill-current' : 'text-slate-200'}`} />
      </button>
    ))}
  </div>
);

const AlumniBusinessServices = () => {
  usePageTitle('Business Services');
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Providers
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [page, setPage] = useState(1);
  const LIMIT = 12;

  // Detail modal
  const [selected, setSelected] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [showQR, setShowQR] = useState(null); // provider_id or null

  // Edit/Delete
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Premium
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [showPremiumPlan, setShowPremiumPlan] = useState(false);

  // Dynamic categories
  const [dynamicCategories, setDynamicCategories] = useState([]);

  // Registration
  const [showRegister, setShowRegister] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [newProvider, setNewProvider] = useState({
    category: 'career_coach', custom_category: '', business_name: '', business_description: '',
    specialties: '', location: '', contact_email: '', contact_phone: '',
    website_url: '', calendly_link: '', pricing: '', linkedin_url: '', instagram_url: '',
    google_business_url: '',
  });

  // Build merged category list
  const allCategories = (() => {
    const defaultValues = new Set(DEFAULT_CATEGORIES.map(c => c.value));
    const extras = dynamicCategories
      .filter(val => val && !defaultValues.has(val))
      .map(val => ({ value: val, label: val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }));
    return [...DEFAULT_CATEGORIES, ...extras];
  })();

  useEffect(() => { fetchProviders(); }, [category, page]);
  useEffect(() => {
    providersAPI.getCategories().then(res => setDynamicCategories(Array.isArray(res.data) ? res.data : [])).catch(() => {});
    providersAPI.premiumStatus().then(r => setPremiumStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const urlCat = searchParams.get('category');
    if (urlCat && urlCat !== category) setCategory(urlCat);
  }, [searchParams]);

  // Auto-open provider detail from URL param (?provider=prov_xxx)
  useEffect(() => {
    const providerId = searchParams.get('provider');
    if (providerId && !selected) {
      providersAPI.get(providerId).then(res => {
        if (res.data) handleSelectProvider(res.data);
      }).catch(() => {});
    }
  }, [searchParams]);

  const getProviderUrl = (providerId) => getShareUrl('provider', providerId);
  const getQRUrl = (providerId, size = 300) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(getProviderUrl(providerId))}&bgcolor=ffffff&color=1e293b&margin=10`;

  const downloadQR = async (providerId, providerName) => {
    try {
      const response = await fetch(getQRUrl(providerId, 600));
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(providerName || 'provider').replace(/\s+/g, '-').toLowerCase()}-qr-code.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('QR code downloaded!');
    } catch { toast.error('Failed to download QR code'); }
  };

  const copyProviderLink = (providerId) => {
    navigator.clipboard.writeText(getProviderUrl(providerId));
    toast.success('Link copied!');
  };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: (page - 1) * LIMIT };
      if (category !== 'all') params.category = category;
      if (search) params.search = search;
      if (locationSearch) params.location = locationSearch;
      const res = await providersAPI.list(params);
      setProviders(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to load providers'); }
    finally { setLoading(false); }
  };

  const handleSelectProvider = async (provider) => {
    setSelected(provider);
    setLoadingReviews(true);
    try {
      const res = await reviewsAPI.list(provider.provider_id);
      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch { setReviews([]); }
    finally { setLoadingReviews(false); }
  };

  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) { toast.error('Please write a review'); return; }
    setSubmittingReview(true);
    try {
      await reviewsAPI.create({ provider_id: selected.provider_id, rating: newReview.rating, comment: newReview.comment });
      toast.success('Review submitted!');
      setShowReviewForm(false);
      setNewReview({ rating: 5, comment: '' });
      const res = await reviewsAPI.list(selected.provider_id);
      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to submit review'); }
    finally { setSubmittingReview(false); }
  };

  const handleRegister = async () => {
    if (!newProvider.business_name || !newProvider.business_description) {
      toast.error('Business name and description are required'); return;
    }
    if (newProvider.category === 'other' && !newProvider.custom_category.trim()) {
      toast.error('Please enter your custom category'); return;
    }
    setRegistering(true);
    try {
      let logoUrl = null;
      if (logoFile) {
        const uploadRes = await uploadAPI.postImage(logoFile);
        logoUrl = uploadRes.data.url;
      }
      const payload = {
        ...newProvider,
        category: newProvider.category === 'other' ? newProvider.custom_category.trim().toLowerCase().replace(/\s+/g, '_') : newProvider.category,
        specialties: newProvider.specialties ? newProvider.specialties.split(',').map(s => s.trim()).filter(Boolean) : [],
        name: user?.name,
        company_logo: logoUrl,
      };
      delete payload.custom_category;
      delete payload.pricing;
      await providersAPI.create(payload);
      toast.success('Provider profile created!');
      setShowRegister(false);
      setLogoFile(null);
      setLogoPreview(null);
      setNewProvider({ category: 'career_coach', custom_category: '', business_name: '', business_description: '', specialties: '', location: '', contact_email: '', contact_phone: '', website_url: '', calendly_link: '', pricing: '', linkedin_url: '', instagram_url: '', google_business_url: '' });
      fetchProviders();
      providersAPI.getCategories().then(res => setDynamicCategories(Array.isArray(res.data) ? res.data : [])).catch(() => {});
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to register'); }
    finally { setRegistering(false); }
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const isOwnerOrAdmin = (provider) => {
    if (!user || !provider) return false;
    const isAdmin = user.role === 'admin' || (user.roles || []).includes('admin');
    return provider.user_id === user.user_id || isAdmin;
  };

  const startEdit = (provider) => {
    setEditData({
      profile_photo: provider.profile_photo || '',
      company_logo: provider.company_logo || '',
      cover_photo: (provider.photos && provider.photos[0]) || '',
      business_name: provider.business_name || '',
      business_description: provider.business_description || '',
      category: provider.category || '',
      specialties: (provider.specialties || []).join(', '),
      credentials: (provider.credentials || []).join(', '),
      location: provider.location || '',
      contact_email: provider.contact_email || '',
      contact_phone: provider.contact_phone || '',
      website_url: provider.website_url || '',
      calendly_link: provider.calendly_link || '',
      linkedin_url: provider.linkedin_url || '',
      instagram_url: provider.instagram_url || '',
      google_business_url: provider.google_business_url || '',
      years_experience: provider.years_experience || '',
    });
    setShowEdit(true);
  };

  const [editPhotoUploading, setEditPhotoUploading] = useState(null);
  const profileFileRef = useRef(null);
  const coverFileRef = useRef(null);
  const logoFileRef = useRef(null);

  const uploadEditPhoto = async (kind, file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast.error('Please select an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB.'); return; }
    setEditPhotoUploading(kind);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/founding-provider-headshot`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      if (kind === 'profile') setEditData(p => ({ ...p, profile_photo: data.url }));
      if (kind === 'cover') setEditData(p => ({ ...p, cover_photo: data.url }));
      if (kind === 'logo') setEditData(p => ({ ...p, company_logo: data.url }));
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setEditPhotoUploading(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!selected || !editData) return;
    setSavingEdit(true);
    try {
      const restPhotos = ((selected?.photos || []).slice(1));
      const newPhotos = editData.cover_photo
        ? [editData.cover_photo, ...restPhotos]
        : restPhotos;
      const payload = {
        ...editData,
        specialties: editData.specialties ? editData.specialties.split(',').map(s => s.trim()).filter(Boolean) : [],
        credentials: editData.credentials ? editData.credentials.split(',').map(s => s.trim()).filter(Boolean) : [],
        years_experience: editData.years_experience ? parseInt(editData.years_experience) : null,
        photos: newPhotos,
      };
      delete payload.cover_photo;
      const res = await providersAPI.update(selected.provider_id, payload);
      toast.success('Listing updated!');
      setShowEdit(false);
      setSelected(res.data);
      fetchProviders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSavingEdit(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await providersAPI.delete(selected.provider_id);
      toast.success('Listing deleted');
      setShowDeleteConfirm(false);
      setSelected(null);
      fetchProviders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
    finally { setDeleting(false); }
  };

  const formatCat = (c) => (c || '').replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Alumni Business Services</h1>
            <p className="text-slate-gray mt-1">Discover and support trusted professionals from the alumni network</p>
          </div>
          <Button onClick={() => setShowRegister(true)} className="bg-dark-slate text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" />List Your Business
          </Button>
        </div>

        {/* Premium banners */}
        {premiumStatus?.has_provider_profile && !premiumStatus?.is_active && (
          <div className="rounded-2xl p-4 mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-sm font-medium text-dark-slate">Upgrade to Premium Provider</p>
                <p className="text-xs text-slate-gray">Get featured placement, profile analytics, and a premium badge — $19/mo</p>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowPremiumPlan(true)} className="rounded-full bg-amber-500 text-white hover:bg-amber-600 text-xs">
              <Crown className="w-3.5 h-3.5 mr-1" />Upgrade
            </Button>
          </div>
        )}

        {/* Service Area Tiles */}
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2 mb-6">
          <button
            onClick={() => { setCategory('all'); setPage(1); }}
            className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all ${
              category === 'all' ? 'bg-dark-slate text-white border-dark-slate shadow-md' : 'bg-white text-slate-gray border-slate-200 hover:border-slate-300 hover:shadow-sm'
            }`}>
            <Briefcase className="w-5 h-5" />
            <span className="text-[10px] font-medium leading-tight">All</span>
          </button>
          {SERVICE_AREAS.map(area => {
            const Icon = area.icon;
            const isActive = category === area.value;
            return (
              <button key={area.value}
                onClick={() => { setCategory(area.value); setPage(1); }}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all ${
                  isActive ? 'bg-dark-slate text-white border-dark-slate shadow-md' : `${area.color} border hover:shadow-sm`
                }`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight">{area.label}</span>
              </button>
            );
          })}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
            <Input placeholder="Search providers..." value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchProviders()} className="pl-10 h-11 rounded-xl bg-white border-slate-200" />
          </div>
          <div className="flex-1 sm:max-w-[200px]">
            <LocationAutocomplete value={locationSearch} onChange={setLocationSearch}
              onSelect={(loc) => { setLocationSearch(loc); setTimeout(fetchProviders, 100); }}
              placeholder="City or zip..." className="h-11 rounded-xl bg-white border-slate-200" />
          </div>
          <Button onClick={fetchProviders} className="h-11 bg-dark-slate text-white rounded-xl px-6">
            <Search className="w-4 h-4 mr-2" />Search
          </Button>
        </div>

        {/* CTA for non-providers */}
        {!premiumStatus?.has_provider_profile && (
          <div className="rounded-2xl mb-6 overflow-hidden border border-amber-200/60 bg-gradient-to-r from-amber-50 via-white to-orange-50">
            <div className="flex flex-col sm:flex-row items-center gap-4 p-5">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-amber-500" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="font-heading font-bold text-dark-slate">Are you a professional? List your business here.</h3>
                <p className="text-sm text-slate-gray mt-0.5">
                  Join for free, or go <span className="font-semibold text-amber-600">Premium</span> for $19/mo to get featured placement, a premium badge, profile analytics, and more.
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button variant="outline" onClick={() => setShowRegister(true)} className="rounded-full text-sm">List Free</Button>
                <Button onClick={() => setShowPremiumPlan(true)} className="rounded-full bg-amber-500 text-white hover:bg-amber-600 text-sm">
                  <Crown className="w-3.5 h-3.5 mr-1" />Go Premium
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Provider Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
        ) : providers.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
            <Briefcase className="w-12 h-12 text-slate-gray mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No providers found</h3>
            <p className="text-slate-gray">Try different search terms or category</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {providers.map(provider => {
                const hbcuName = getHbcuFromCredentials(provider.credentials);
                const hbcuLogoUrl = hbcuName ? getHbcuLogoUrl(hbcuName) : null;
                const hasTopBadge = provider.premium || isFoundingProvider(provider.credentials);
                const hasHeaderImage = provider.company_logo || provider.photos?.length > 0;
                return (
                <div key={provider.provider_id}
                  className={`relative bg-white rounded-2xl border shadow-ambient hover:shadow-hover transition-shadow overflow-hidden cursor-pointer ${
                    provider.premium ? 'border-amber-200 ring-1 ring-amber-100' : 'border-slate-100'
                  }`}
                  onClick={() => handleSelectProvider(provider)}>

                  {/* Top-right badges (always pinned, above the heart) */}
                  {(provider.premium || isFoundingProvider(provider.credentials)) && (
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
                      {isFoundingProvider(provider.credentials) && (
                        <Badge className="bg-mint-fresh text-dark-slate border-0 text-[10px] shadow-sm">
                          <Award className="w-3 h-3 mr-0.5" />GradLink Founding Provider
                        </Badge>
                      )}
                      {provider.premium && (
                        <Badge className="bg-amber-500 text-white border-0 text-[10px] shadow-sm">
                          <Star className="w-3 h-3 mr-0.5 fill-current" />Premium
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Card header with logo/photos */}
                  {(provider.company_logo || provider.photos?.length > 0) && (
                    <div className="relative h-32 bg-gradient-to-br from-slate-50 to-slate-100">
                      {provider.photos?.[0] ? (
                        <img src={provider.photos[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <img src={provider.company_logo} alt="" className="w-20 h-20 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                      )}
                    </div>
                  )}

                  <div className={hasTopBadge && !hasHeaderImage ? 'pt-12 px-5 pb-5' : 'p-5'}>
                    <div className="flex items-start space-x-3 mb-3">
                      <Avatar className="w-20 h-20 flex-shrink-0">
                        <AvatarImage src={provider.profile_photo || provider.photos?.[0]} className="object-cover" />
                        <AvatarFallback className="bg-mint-fresh text-dark-slate text-2xl">{provider.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <h3 className="font-bold text-dark-slate line-clamp-1">{provider.name}</h3>
                          {provider.verified && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                        </div>
                        {provider.business_name && <p className="text-sm text-slate-gray line-clamp-1">{provider.business_name}</p>}
                        {hbcuName && (
                          <div className="flex items-center gap-1.5 mt-1">
                            {hbcuLogoUrl ? (
                              <CredentialLogo src={hbcuLogoUrl} fallback={GraduationCap} />
                            ) : (
                              <GraduationCap className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                            )}
                            <p className="text-xs text-emerald-700 font-medium line-clamp-1">{hbcuName}</p>
                          </div>
                        )}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); toggleFavorite(provider.provider_id); }} className="p-1.5 rounded-full hover:bg-slate-100">
                        <Heart className={`w-4 h-4 ${favorites.has(provider.provider_id) ? 'text-rose-500 fill-current' : 'text-slate-gray'}`} />
                      </button>
                    </div>

                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                      <Badge variant="secondary" className="bg-slate-100 text-xs">{formatCat(provider.category)}</Badge>
                      {provider.google_business_url && (
                        <a href={extUrl(provider.google_business_url)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          <Badge variant="outline" className="text-[10px] gap-0.5 hover:bg-slate-50">
                            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Google Reviews
                          </Badge>
                        </a>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 mb-2">
                      <StarRating rating={provider.average_rating || 0} />
                      <span className="text-xs text-slate-gray">({provider.total_reviews || 0})</span>
                    </div>

                    {provider.location && (
                      <p className="text-xs text-slate-gray flex items-center mb-2"><MapPin className="w-3 h-3 mr-1" />{provider.location}</p>
                    )}

                    {provider.specialties?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {provider.specialties.slice(0, 3).map((s, i) => <Badge key={i} variant="outline" className="text-[10px]">{s}</Badge>)}
                        {provider.specialties.length > 3 && <span className="text-[10px] text-slate-gray">+{provider.specialties.length - 3}</span>}
                      </div>
                    )}

                    {/* Social links row */}
                    {(provider.website_url || provider.linkedin_url || provider.instagram_url) && (
                      <div className="flex items-center gap-2 mb-3">
                        {provider.website_url && (
                          <a href={extUrl(provider.website_url)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                            className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-dark-slate transition-colors">
                            <Globe className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {provider.linkedin_url && (
                          <a href={extUrl(provider.linkedin_url)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                            className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors">
                            <Linkedin className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {provider.instagram_url && (
                          <a href={extUrl(provider.instagram_url)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                            className="w-7 h-7 rounded-full bg-pink-50 flex items-center justify-center text-pink-600 hover:bg-pink-100 transition-colors">
                            <Instagram className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    )}

                    <Button size="sm" className="w-full rounded-full bg-dark-slate text-white mt-1" onClick={(e) => { e.stopPropagation(); handleSelectProvider(provider); }}>
                      View Details
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center space-x-3">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-gray font-medium">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={providers.length < LIMIT} className="rounded-full">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}

        {/* ═══════════ Provider Detail Modal ═══════════ */}
        <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setShowReviewForm(false); }}>
          <DialogContent className="max-w-4xl rounded-2xl max-h-[92vh] overflow-y-auto">
            {selected && (
              <>
                {/* Hero photo: prefer cover photo, then profile photo as a large portrait */}
                {selected.photos?.[0] ? (
                  <img src={selected.photos[0]} alt="" className="w-full h-44 object-cover rounded-xl mb-4" />
                ) : selected.profile_photo ? (
                  <div className="w-full mb-4 flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl py-6">
                    <img src={selected.profile_photo} alt={selected.name} className="w-44 h-44 rounded-full object-cover border-4 border-white shadow-lg" />
                  </div>
                ) : null}

                <DialogHeader>
                  <div className="flex items-start gap-4">
                    {selected.company_logo ? (
                      <img src={selected.company_logo} alt="" className="w-20 h-20 rounded-xl object-contain bg-slate-50 p-2 border border-slate-100 flex-shrink-0" />
                    ) : (
                      <Avatar className="w-20 h-20 flex-shrink-0">
                        <AvatarImage src={selected.profile_photo || selected.photos?.[0]} className="object-cover" />
                        <AvatarFallback className="bg-mint-fresh text-dark-slate text-2xl">{selected.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <DialogTitle className="font-heading text-2xl">{selected.name}</DialogTitle>
                        {selected.verified && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                        {isFoundingProvider(selected.credentials) && (
                          <Badge className="text-xs bg-mint-fresh/30 text-emerald-800 border border-mint-fresh/50">
                            <Award className="w-3 h-3 mr-0.5" />Founding Provider
                          </Badge>
                        )}
                        {selected.premium && <Badge className="text-xs bg-amber-100 text-amber-700 border border-amber-200"><Star className="w-3 h-3 mr-0.5 fill-current" />Premium</Badge>}
                      </div>
                      {selected.business_name && <p className="text-slate-gray">{selected.business_name}</p>}
                    </div>
                  </div>
                </DialogHeader>

                {/* Edit / Delete actions for owner or admin */}
                {isOwnerOrAdmin(selected) && (
                  <div className="flex items-center gap-2 mt-2">
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => startEdit(selected)}>
                      <Edit2 className="w-3.5 h-3.5 mr-1" />Edit Listing
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-xs text-red-600 hover:bg-red-50 border-red-200" onClick={() => setShowDeleteConfirm(true)}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                    </Button>
                  </div>
                )}

                <div className="space-y-5 mt-4">
                  {/* Rating */}
                  <div className="flex items-center space-x-3">
                    <StarRating rating={selected.average_rating || 0} size="md" />
                    <span className="text-sm text-slate-gray">{selected.average_rating?.toFixed(1) || '0'} ({selected.total_reviews || 0} reviews)</span>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{formatCat(selected.category)}</Badge>
                    {selected.location && <Badge variant="secondary" className="bg-slate-100"><MapPin className="w-3 h-3 mr-1" />{selected.location}</Badge>}
                    {selected.years_experience && <Badge variant="secondary" className="bg-blue-50 text-blue-700">{selected.years_experience}+ years exp</Badge>}
                  </div>

                  {selected.specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selected.specialties.map((s, i) => <Badge key={i} variant="outline">{s}</Badge>)}
                    </div>
                  )}

                  {/* Video intro */}
                  {selected.video_intro && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2 flex items-center gap-1"><Video className="w-4 h-4" />Video Introduction</h4>
                      <video src={selected.video_intro} controls className="w-full rounded-xl max-h-64 bg-black" />
                    </div>
                  )}

                  {/* About */}
                  {selected.business_description && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">About</h4>
                      <p className="text-slate-gray text-sm whitespace-pre-wrap">{selected.business_description}</p>
                    </div>
                  )}

                  {/* Portfolio */}
                  {selected.portfolio?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">Portfolio</h4>
                      <div className="grid grid-cols-3 gap-2">
                        {selected.portfolio.map((img, i) => (
                          <img key={i} src={img} alt={`Portfolio ${i + 1}`} className="w-full aspect-square object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(img, '_blank')} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gallery photos */}
                  {selected.photos?.length > 1 && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">Photos</h4>
                      <div className="grid grid-cols-3 gap-2">
                        {selected.photos.slice(1).map((img, i) => (
                          <img key={i} src={img} alt="" className="w-full aspect-video object-cover rounded-xl cursor-pointer hover:opacity-90"
                            onClick={() => window.open(img, '_blank')} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Credentials */}
                  {selected.credentials?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">Credentials</h4>
                      <div className="flex flex-wrap gap-2">
                        {selected.credentials.map((c, i) => {
                          const { icon: Icon, label, logoUrl, classes } = credentialDisplay(c);
                          return (
                            <span key={i} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border ${classes}`}>
                              {logoUrl ? <CredentialLogo src={logoUrl} fallback={Icon} /> : <Icon className="w-4 h-4 flex-shrink-0" />}
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Digital business card */}
                  {selected.digital_business_card && (
                    <a href={selected.digital_business_card} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-dark-slate flex items-center justify-center">
                        <Link2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-dark-slate">Digital Business Card</p>
                        <p className="text-xs text-slate-gray">Tap to view contact info</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-gray ml-auto" />
                    </a>
                  )}

                  {/* Contact & social buttons */}
                  <div className="flex flex-wrap gap-2">
                    {selected.calendly_link && (
                      <a href={extUrl(selected.calendly_link)} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-dark-slate text-white rounded-full"><Calendar className="w-4 h-4 mr-2" />Schedule a Meeting</Button>
                      </a>
                    )}
                    {selected.contact_email && (
                      <a href={`mailto:${selected.contact_email}`}>
                        <Button variant="outline" className="rounded-full"><Mail className="w-4 h-4 mr-2" />Email</Button>
                      </a>
                    )}
                    {selected.contact_phone && (
                      <a href={`tel:${selected.contact_phone}`}>
                        <Button variant="outline" className="rounded-full"><Phone className="w-4 h-4 mr-2" />Call</Button>
                      </a>
                    )}
                    {selected.website_url && (
                      <a href={extUrl(selected.website_url)} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="rounded-full"><Globe className="w-4 h-4 mr-2" />Website</Button>
                      </a>
                    )}
                  </div>

                  {/* Social links */}
                  {(selected.linkedin_url || selected.instagram_url || selected.twitter_url || selected.tiktok_url) && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-gray font-medium">Follow:</span>
                      {selected.linkedin_url && (
                        <a href={extUrl(selected.linkedin_url)} target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {selected.instagram_url && (
                        <a href={extUrl(selected.instagram_url)} target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 rounded-full bg-pink-50 flex items-center justify-center text-pink-600 hover:bg-pink-100 transition-colors">
                          <Instagram className="w-4 h-4" />
                        </a>
                      )}
                      {selected.twitter_url && (
                        <a href={extUrl(selected.twitter_url)} target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors">
                          <X className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* QR Code & Share */}
                  <div className="border-t border-slate-100 pt-5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-dark-slate flex items-center gap-2">
                        <QrCode className="w-4 h-4" />Share This Business
                      </h4>
                    </div>
                    {showQR === selected.provider_id ? (
                      <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <img
                          src={getQRUrl(selected.provider_id)}
                          alt="QR Code"
                          className="w-48 h-48 rounded-lg"
                        />
                        <p className="text-xs text-slate-gray text-center">Scan to view {selected.business_name || selected.name}'s profile</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => downloadQR(selected.provider_id, selected.business_name || selected.name)}>
                            <Download className="w-3.5 h-3.5 mr-1" />Download
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => copyProviderLink(selected.provider_id)}>
                            <Copy className="w-3.5 h-3.5 mr-1" />Copy Link
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setShowQR(null)}>
                            Hide
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <ShareMenu url={getProviderUrl(selected.provider_id)} title={selected.business_name || selected.name} />
                        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => setShowQR(selected.provider_id)}>
                          <QrCode className="w-3.5 h-3.5 mr-1.5" />Show QR Code
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Google Reviews */}
                  {selected.google_business_url && (
                    <div className="border-t border-slate-100 pt-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          <h4 className="font-semibold text-dark-slate">Google Reviews</h4>
                        </div>
                        <a href={extUrl(selected.google_business_url)} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="rounded-full text-xs">
                            View on Google <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        </a>
                      </div>
                      <a href={extUrl(selected.google_business_url)} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-1">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className="w-4 h-4 text-amber-400 fill-current" />
                          ))}
                        </div>
                        <span className="text-sm text-slate-gray">See what customers are saying on Google</span>
                        <ExternalLink className="w-3.5 h-3.5 text-slate-400 ml-auto flex-shrink-0" />
                      </a>
                    </div>
                  )}

                  {/* GradLink Reviews */}
                  <div className="border-t border-slate-100 pt-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded bg-dark-slate flex items-center justify-center">
                          <span className="text-white text-[9px] font-bold">GL</span>
                        </div>
                        <h4 className="font-semibold text-dark-slate">GradLink Reviews ({reviews.length})</h4>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setShowReviewForm(!showReviewForm)} className="rounded-full text-xs">Write Review</Button>
                    </div>
                    {showReviewForm && (
                      <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3">
                        <div><Label className="text-sm">Rating</Label><StarRating rating={newReview.rating} size="md" interactive onChange={(r) => setNewReview(p => ({ ...p, rating: r }))} /></div>
                        <Textarea value={newReview.comment} onChange={(e) => setNewReview(p => ({ ...p, comment: e.target.value }))} placeholder="Share your experience..." rows={3} />
                        <Button onClick={handleSubmitReview} disabled={submittingReview} className="bg-dark-slate text-white rounded-full">
                          {submittingReview ? 'Submitting...' : 'Submit Review'}
                        </Button>
                      </div>
                    )}
                    {loadingReviews ? (
                      <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-gray mx-auto" /></div>
                    ) : reviews.length === 0 ? (
                      <p className="text-slate-gray text-sm text-center py-4">No reviews yet. Be the first!</p>
                    ) : (
                      <div className="space-y-3">
                        {reviews.map(review => (
                          <div key={review.review_id} className="bg-slate-50 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-dark-slate text-sm">{review.user_name}</span>
                                <StarRating rating={review.rating} />
                              </div>
                              {review.created_at && <span className="text-xs text-slate-gray">{formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}</span>}
                            </div>
                            <p className="text-sm text-slate-gray">{review.comment}</p>
                            {review.provider_response && (
                              <div className="mt-2 pl-3 border-l-2 border-emerald-300">
                                <p className="text-xs font-medium text-emerald-700">Provider Response</p>
                                <p className="text-sm text-slate-gray">{review.provider_response}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ═══════════ Premium Plan Dialog ═══════════ */}
        <Dialog open={showPremiumPlan} onOpenChange={setShowPremiumPlan}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-2xl text-dark-slate">Premium Provider</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-gray mb-4">Stand out from the crowd with top placement and powerful tools.</p>
            <div className="rounded-2xl p-5 border-2 border-amber-300 bg-amber-50/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⭐</span>
                  <div><h3 className="font-heading font-bold text-lg text-dark-slate">Premium</h3><p className="text-xs text-slate-gray">14-day free trial</p></div>
                </div>
                <div className="text-right"><p className="font-bold text-2xl text-dark-slate">$19</p><p className="text-xs text-slate-gray">/month</p></div>
              </div>
              <ul className="space-y-1.5 mb-4">
                {['Premium badge & priority placement', 'Boosted to top of search results', 'Profile view analytics', 'Video introduction on your profile', 'Portfolio gallery showcase', 'Homepage spotlight rotation'].map(perk => (
                  <li key={perk} className="flex items-center gap-2 text-sm text-slate-gray"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{perk}</li>
                ))}
              </ul>
              {premiumStatus?.is_active ? (
                <Button disabled className="w-full rounded-full bg-slate-100 text-slate-500">Current Plan</Button>
              ) : (
                <Button onClick={async () => {
                  try { const res = await providersAPI.premiumSubscribe({ origin_url: window.location.origin }); if (res.data?.checkout_url) window.location.href = res.data.checkout_url; }
                  catch (e) { toast.error(e.response?.data?.detail || 'Subscription failed'); }
                }} className="w-full rounded-full bg-amber-500 text-white hover:bg-amber-600">
                  Start Free Trial <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ═══════════ Register Dialog ═══════════ */}
        <Dialog open={showRegister} onOpenChange={setShowRegister}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-2xl text-dark-slate">List Your Business</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-gray mb-4">Register as a service provider to be listed in the directory and connect with alumni.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category *</Label>
                  <Select value={newProvider.category} onValueChange={(v) => setNewProvider(p => ({ ...p, category: v, custom_category: '' }))}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {allCategories.filter(c => c.value !== 'all').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      <SelectItem value="other">Other (Custom)</SelectItem>
                    </SelectContent>
                  </Select>
                  {newProvider.category === 'other' && (
                    <Input value={newProvider.custom_category} onChange={(e) => setNewProvider(p => ({ ...p, custom_category: e.target.value }))}
                      placeholder="Enter your category" className="mt-2 rounded-xl" />
                  )}
                </div>
                <div>
                  <Label>Business Name *</Label>
                  <Input value={newProvider.business_name} onChange={(e) => setNewProvider(p => ({ ...p, business_name: e.target.value }))}
                    placeholder="Your business name" className="mt-1 rounded-xl" />
                </div>
              </div>

              {/* Logo upload */}
              <div>
                <Label>Business Logo</Label>
                <div className="mt-1 flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img src={logoPreview} alt="Logo preview" className="w-16 h-16 rounded-xl object-contain bg-slate-50 p-2 border border-slate-200" />
                      <button type="button" onClick={() => { if (logoPreview) URL.revokeObjectURL(logoPreview); setLogoFile(null); setLogoPreview(null); }}
                        className="absolute -top-1.5 -right-1.5 bg-dark-slate text-white rounded-full p-0.5 hover:bg-red-500 transition-colors shadow-sm">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-slate-300" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" className="rounded-full text-xs" type="button" asChild>
                        <span><Camera className="w-3.5 h-3.5 mr-1.5" />Upload Logo</span>
                      </Button>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) { toast.error('Logo too large. Max 5MB'); return; }
                        setLogoFile(file);
                        setLogoPreview(URL.createObjectURL(file));
                      }} />
                    </label>
                    <p className="text-[11px] text-slate-gray mt-1">Square image recommended (PNG, JPG). Max 5MB.</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea value={newProvider.business_description} onChange={(e) => setNewProvider(p => ({ ...p, business_description: e.target.value }))}
                  placeholder="Describe your services..." rows={3} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Specialties (comma separated)</Label>
                <Input value={newProvider.specialties} onChange={(e) => setNewProvider(p => ({ ...p, specialties: e.target.value }))}
                  placeholder="e.g. Tax Planning, Estate Law" className="mt-1 rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Location</Label><Input value={newProvider.location} onChange={(e) => setNewProvider(p => ({ ...p, location: e.target.value }))} placeholder="Atlanta, GA" className="mt-1 rounded-xl" /></div>
                <div><Label>Pricing</Label><Input value={newProvider.pricing} onChange={(e) => setNewProvider(p => ({ ...p, pricing: e.target.value }))} placeholder="$100/hr" className="mt-1 rounded-xl" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={newProvider.contact_email} onChange={(e) => setNewProvider(p => ({ ...p, contact_email: e.target.value }))} placeholder="contact@example.com" className="mt-1 rounded-xl" /></div>
                <div><Label>Phone</Label><Input value={newProvider.contact_phone} onChange={(e) => setNewProvider(p => ({ ...p, contact_phone: e.target.value }))} placeholder="(555) 123-4567" className="mt-1 rounded-xl" /></div>
              </div>
              <div><Label>Website</Label><Input value={newProvider.website_url} onChange={(e) => setNewProvider(p => ({ ...p, website_url: e.target.value }))} placeholder="https://yoursite.com" className="mt-1 rounded-xl" /></div>
              <div><Label>External Calendar Link <span className="text-slate-gray text-xs font-normal">(Calendly, Acuity, etc.)</span></Label>
                <Input value={newProvider.calendly_link} onChange={(e) => setNewProvider(p => ({ ...p, calendly_link: e.target.value }))} placeholder="https://calendly.com/yourname" className="mt-1 rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>LinkedIn</Label><Input value={newProvider.linkedin_url} onChange={(e) => setNewProvider(p => ({ ...p, linkedin_url: e.target.value }))} placeholder="https://linkedin.com/in/you" className="mt-1 rounded-xl" /></div>
                <div><Label>Instagram</Label><Input value={newProvider.instagram_url} onChange={(e) => setNewProvider(p => ({ ...p, instagram_url: e.target.value }))} placeholder="https://instagram.com/you" className="mt-1 rounded-xl" /></div>
              </div>
              <div>
                <Label>Google Business Profile <span className="text-slate-gray text-xs font-normal">(for Google Reviews)</span></Label>
                <Input value={newProvider.google_business_url} onChange={(e) => setNewProvider(p => ({ ...p, google_business_url: e.target.value }))}
                  placeholder="https://g.co/kgs/your-business" className="mt-1 rounded-xl" />
              </div>
              <Button onClick={handleRegister} disabled={registering} className="w-full h-12 bg-dark-slate text-white rounded-full font-semibold">
                {registering ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registering...</> : <><Plus className="w-4 h-4 mr-2" />List Your Business</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        {/* ═══════════ Edit Listing Dialog ═══════════ */}
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-2xl text-dark-slate">Edit Listing</DialogTitle></DialogHeader>
            {editData && (
              <div className="space-y-4 mt-4">
                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-dark-slate">Photos</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { kind: 'profile', label: 'Headshot', value: editData.profile_photo, onUrlChange: (v) => setEditData(p => ({ ...p, profile_photo: v })), ref: profileFileRef, shape: 'rounded-full' },
                      { kind: 'cover', label: 'Cover photo', value: editData.cover_photo, onUrlChange: (v) => setEditData(p => ({ ...p, cover_photo: v })), ref: coverFileRef, shape: 'rounded-xl', wide: true },
                      { kind: 'logo', label: 'Company logo', value: editData.company_logo, onUrlChange: (v) => setEditData(p => ({ ...p, company_logo: v })), ref: logoFileRef, shape: 'rounded-xl' },
                    ].map(field => (
                      <div key={field.kind} className="space-y-2">
                        <p className="text-xs font-medium text-slate-gray">{field.label}</p>
                        <div className={`${field.wide ? 'aspect-[3/2]' : 'aspect-square'} bg-slate-50 border-2 border-dashed border-slate-200 ${field.shape} overflow-hidden flex items-center justify-center cursor-pointer hover:border-slate-300`} onClick={() => field.ref.current?.click()}>
                          {field.value ? (
                            <img src={field.value} alt={field.label} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <div className="text-center text-slate-400">
                              {editPhotoUploading === field.kind ? <Loader2 className="w-5 h-5 mx-auto animate-spin" /> : <Upload className="w-5 h-5 mx-auto" />}
                              <p className="text-[10px] mt-1">Click to upload</p>
                            </div>
                          )}
                          <input ref={field.ref} type="file" accept="image/*" className="hidden" disabled={!!editPhotoUploading} onChange={(e) => uploadEditPhoto(field.kind, e.target.files?.[0])} />
                        </div>
                        <Input value={field.value || ''} onChange={(e) => field.onUrlChange(e.target.value)} placeholder="...or paste URL" className="rounded-lg text-xs h-8" />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Business Name</Label>
                    <Input value={editData.business_name} onChange={(e) => setEditData(p => ({ ...p, business_name: e.target.value }))} className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={editData.category} onValueChange={(v) => setEditData(p => ({ ...p, category: v }))}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {allCategories.filter(c => c.value !== 'all').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={editData.business_description} onChange={(e) => setEditData(p => ({ ...p, business_description: e.target.value }))} rows={4} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Specialties (comma separated)</Label>
                  <Input value={editData.specialties} onChange={(e) => setEditData(p => ({ ...p, specialties: e.target.value }))} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>HBCU Affiliations & Credentials</Label>
                  <Input value={editData.credentials} onChange={(e) => setEditData(p => ({ ...p, credentials: e.target.value }))} placeholder="HBCU Alum: Southern University of New Orleans, GradLink Founding Provider" className="mt-1 rounded-xl" />
                  <p className="text-xs text-slate-gray mt-1">Comma-separated. For HBCU affiliations, prefix with <span className="font-mono">HBCU Alum:</span> so the school logo appears. Spell out school names in full.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Location</Label><Input value={editData.location} onChange={(e) => setEditData(p => ({ ...p, location: e.target.value }))} className="mt-1 rounded-xl" /></div>
                  <div><Label>Years Experience</Label><Input type="number" value={editData.years_experience} onChange={(e) => setEditData(p => ({ ...p, years_experience: e.target.value }))} className="mt-1 rounded-xl" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input value={editData.contact_email} onChange={(e) => setEditData(p => ({ ...p, contact_email: e.target.value }))} className="mt-1 rounded-xl" /></div>
                  <div><Label>Phone</Label><Input value={editData.contact_phone} onChange={(e) => setEditData(p => ({ ...p, contact_phone: e.target.value }))} className="mt-1 rounded-xl" /></div>
                </div>
                <div><Label>Website</Label><Input value={editData.website_url} onChange={(e) => setEditData(p => ({ ...p, website_url: e.target.value }))} className="mt-1 rounded-xl" /></div>
                <div><Label>External Calendar Link</Label><Input value={editData.calendly_link} onChange={(e) => setEditData(p => ({ ...p, calendly_link: e.target.value }))} className="mt-1 rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>LinkedIn</Label><Input value={editData.linkedin_url} onChange={(e) => setEditData(p => ({ ...p, linkedin_url: e.target.value }))} className="mt-1 rounded-xl" /></div>
                  <div><Label>Instagram</Label><Input value={editData.instagram_url} onChange={(e) => setEditData(p => ({ ...p, instagram_url: e.target.value }))} className="mt-1 rounded-xl" /></div>
                </div>
                <div><Label>Google Business Profile</Label><Input value={editData.google_business_url} onChange={(e) => setEditData(p => ({ ...p, google_business_url: e.target.value }))} className="mt-1 rounded-xl" /></div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleSaveEdit} disabled={savingEdit} className="flex-1 h-11 bg-dark-slate text-white rounded-full">
                    {savingEdit ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowEdit(false)} className="h-11 rounded-full px-6">Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ═══════════ Delete Confirmation Dialog ═══════════ */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-sm rounded-2xl">
            <DialogHeader><DialogTitle className="text-lg text-dark-slate">Delete Listing?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-gray">
              Are you sure you want to delete <span className="font-semibold text-dark-slate">{selected?.business_name || selected?.name}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-3 mt-4">
              <Button onClick={handleDelete} disabled={deleting} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full">
                {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" />Delete</>}
              </Button>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="rounded-full px-6">Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AlumniBusinessServices;
